/**
 * @fileoverview Client-side error logging service
 * @module composables/useErrorLogging
 *
 * Provides centralized error logging with context capture.
 * Supports console logging and can be extended for external services.
 */
import { ref } from 'vue'
import type { App } from 'vue'
import { logger } from '@/utils/logger'

export interface ErrorContext {
  /** Component name where error occurred */
  component?: string
  /** Additional error info */
  info?: string
  /** Current URL */
  url?: string
  /** User ID (if authenticated) */
  userId?: string
  /** Tenant ID (if in tenant context) */
  tenantId?: string
  /** Additional metadata */
  metadata?: Record<string, unknown>
}

export interface LoggedError {
  /** Error message */
  message: string
  /** Error stack trace */
  stack?: string
  /** Error context */
  context: ErrorContext
  /** Timestamp */
  timestamp: number
  /** Unique error ID */
  id: string
}

/** In-memory error log (most recent 100 errors) */
const errorLog = ref<LoggedError[]>([])
const MAX_ERROR_LOG = 100

/**
 * Generate unique error ID
 */
function generateErrorId(): string {
  return `err_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Error logging composable
 */
export function useErrorLogging() {
  /**
   * Log an error with context
   */
  function logError(error: Error | string, context: ErrorContext = {}): string {
    const errorObj = typeof error === 'string' ? new Error(error) : error
    const errorId = generateErrorId()

    const loggedError: LoggedError = {
      id: errorId,
      message: errorObj.message,
      stack: errorObj.stack,
      context: {
        ...context,
        url: context.url ?? window.location.href
      },
      timestamp: Date.now()
    }

    // Add to in-memory log
    errorLog.value.unshift(loggedError)
    if (errorLog.value.length > MAX_ERROR_LOG) {
      errorLog.value.pop()
    }

    // Console log in development
    if (import.meta.env.DEV) {
      console.group(`🔴 Error [${errorId}]`)
      console.error(errorObj)
      console.table(loggedError.context)
      console.groupEnd()
    } else {
      // Production: structured log
      console.error(JSON.stringify({
        type: 'client_error',
        errorId,
        message: errorObj.message,
        context: loggedError.context,
        timestamp: new Date().toISOString()
      }))
    }

    // TODO: Send to external error tracking service (Sentry, LogRocket, etc.)
    // sendToErrorService(loggedError)

    return errorId
  }

  /**
   * Log a warning
   */
  function logWarning(message: string, context: ErrorContext = {}): void {
    if (import.meta.env.DEV) {
      console.warn(`⚠️ Warning: ${message}`, context)
    } else {
      console.warn(JSON.stringify({
        type: 'client_warning',
        message,
        context,
        timestamp: new Date().toISOString()
      }))
    }
  }

  /**
   * Log an info message
   */
  function logInfo(message: string, context: ErrorContext = {}): void {
    logger.info(`ℹ️ Info: ${message}`, context)
  }

  /**
   * Get recent errors
   */
  function getRecentErrors(): LoggedError[] {
    return errorLog.value
  }

  /**
   * Clear error log
   */
  function clearErrorLog(): void {
    errorLog.value = []
  }

  /**
   * Export errors as JSON
   */
  function exportErrors(): string {
    return JSON.stringify(errorLog.value, null, 2)
  }

  return {
    logError,
    logWarning,
    logInfo,
    getRecentErrors,
    clearErrorLog,
    exportErrors
  }
}

/**
 * Install global error handlers
 * Call this in main.ts to catch all unhandled errors
 */
export function installGlobalErrorHandlers(app: App): void {
  const { logError } = useErrorLogging()

  // Vue error handler
  app.config.errorHandler = (err, instance, info) => {
    logError(err as Error, {
      component: instance?.$options?.name ?? 'Unknown',
      info
    })
  }

  // Vue warning handler (dev only)
  if (import.meta.env.DEV) {
    app.config.warnHandler = (msg, instance, trace) => {
      console.warn(`Vue Warning: ${msg}`, {
        component: instance?.$options?.name,
        trace
      })
    }
  }

  // Global unhandled error
  window.addEventListener('error', (event) => {
    logError(event.error ?? new Error(event.message), {
      info: 'window.onerror',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    })
  })

  // Unhandled promise rejection
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason))

    logError(error, {
      info: 'unhandledrejection'
    })
  })
}
